import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import type { InvestigationRunStatus } from '../types';
import { ensureSreFontsLoaded } from '../styles/theme';

export interface InvestigationRunStatsProps {
  status: InvestigationRunStatus;
  isStreaming: boolean;
  toolCallsCount: number;
  startedAt: Date | null;
  completedAt: Date | null;
  triggeredBy: string;
  className?: string;
}

function formatRunDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds) || seconds < 0) return '—';
  const whole = Math.floor(seconds);
  if (whole < 60) return `${whole}s`;
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
}

const InvestigationRunStats: React.FC<InvestigationRunStatsProps> = ({
  status,
  isStreaming,
  toolCallsCount,
  startedAt,
  completedAt,
  triggeredBy,
  className = '',
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  useEffect(() => {
    if (!isStreaming || !startedAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isStreaming, startedAt]);

  if (!startedAt && status === 'idle') return null;

  const endMs = completedAt?.getTime() ?? (isStreaming ? now : null);
  const durationSeconds =
    startedAt && endMs != null
      ? Math.max(0, (endMs - startedAt.getTime()) / 1000)
      : null;

  const metrics = [
    {
      key: 'mttd',
      label: 'MTTD',
      sublabel: 'Run duration',
      value: formatRunDuration(durationSeconds),
      spinning: isStreaming,
      help: 'Mean time to detect — how long analysis has been running.',
    },
    {
      key: 'tools',
      label: 'Evidence steps',
      sublabel: 'Tool calls',
      value: isStreaming && toolCallsCount === 0 ? '—' : String(toolCallsCount),
      spinning: isStreaming,
      help: 'Number of diagnostic tools executed during this run.',
    },
    {
      key: 'trigger',
      label: 'Triggered by',
      sublabel: 'Source',
      value: triggeredBy || 'system',
      spinning: false,
      help: 'Operator or system that started the investigation.',
    },
  ];

  return (
    <div
      className={[
        'sre-wb-fade grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {metrics.map((m) => (
        <div
          key={m.key}
          className="relative bg-white px-4 py-3.5 before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-primary-500"
        >
          <div className="mb-1.5 flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {m.label}
            </span>
            <span title={m.help} className="text-slate-300 hover:text-slate-500">
              <QuestionMarkCircleIcon className="h-3.5 w-3.5" />
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <p
              className="truncate font-['IBM_Plex_Mono',ui-monospace,monospace] text-xl font-medium tracking-tight text-slate-900"
              title={m.value}
            >
              {m.value}
            </p>
            {m.spinning && (
              <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin text-primary-600" />
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-400">{m.sublabel}</p>
        </div>
      ))}
    </div>
  );
};

export default InvestigationRunStats;
